/**
 * Console colour scheme (§13.3, D-2601): light-leaning by default — "system" resolves to light unless the OS asks
 * for dark — with an explicit light / dark choice kept on this device only. Applied as `data-theme` on <html>
 * before the first paint (main.tsx) so the kit tokens (`@windrop/ui/theme.css`) and the console tokens
 * (`styles/app.css`) agree. No inline script: this runs from the module bundle (CSP).
 */
import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'windrop:console-theme';
const PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

const DARK_QUERY = '(prefers-color-scheme: dark)';

export function readThemePreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return raw && (PREFERENCES as readonly string[]).includes(raw) ? (raw as ThemePreference) : 'system';
  } catch {
    return 'system';
  }
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference !== 'system') return preference;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

/** Writes `data-theme` + `color-scheme` on <html>. Idempotent; safe to call on every change. */
export function applyConsoleTheme(preference: ThemePreference = readThemePreference()): ResolvedTheme {
  const resolved = resolveTheme(preference);
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    root.setAttribute('data-theme', resolved);
    root.style.colorScheme = resolved;
  }
  return resolved;
}

export function writeThemePreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    /* storage unavailable: the choice lasts for this page only */
  }
  applyConsoleTheme(preference);
}

/** Keeps "system" in step with the OS while the console is open. Returns an unsubscribe. */
export function watchSystemTheme(): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const mq = window.matchMedia(DARK_QUERY);
  const onChange = () => {
    if (readThemePreference() === 'system') applyConsoleTheme('system');
  };
  mq.addEventListener?.('change', onChange);
  return () => mq.removeEventListener?.('change', onChange);
}

export function useConsoleTheme(): {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (p: ThemePreference) => void;
} {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readThemePreference());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(preference));
  useEffect(() => {
    setResolved(applyConsoleTheme(preference));
    if (preference !== 'system') return;
    const stop = watchSystemTheme();
    const mq = typeof window !== 'undefined' ? window.matchMedia?.(DARK_QUERY) : undefined;
    const onChange = () => setResolved(resolveTheme('system'));
    mq?.addEventListener?.('change', onChange);
    return () => {
      stop();
      mq?.removeEventListener?.('change', onChange);
    };
  }, [preference]);
  const setPreference = useCallback((p: ThemePreference) => {
    writeThemePreference(p);
    setPreferenceState(p);
  }, []);
  return { preference, resolved, setPreference };
}
